import { GViewInfo, ProjectDatasetResponse, API_PATHS, NormalizedDataset, apiHost } from 'api';
import {
    actionFetchAndLoadListDatasetsByProjectId,
    actionFetchAndLoadPanoramasByDatasetId,
    checkFilename,
} from './fetchDatasets';
import { getAuthToken } from './getAuthToken';
import { OPEN_STREET_MAP_PROJECTION } from 'constants/index';

const {
    setProjectId,
    setProjectPath,
    setProjectStorgName,
    setShareId,
    setShareToken,
    setProjectToken,
    setPermission,
    setIsEmbed,
    setIsClean,
    setOrgId,
    setTeamId,
    setOrgName,
    setOrgWebsite,
    setCopyrightDate,
    setWatermark,
    setWatermarkText,
    setDatasetId,
    setSnapshotId,
    getOrgId,
    setUserId,
    getCopyrightDate,
    getShareToken,
    setAuthToken,
    setUserAuthType,
    setViewerReqType,
    getSnapshotId,
    getShareId,
    setLWKId,
    setOBJId,
    setOrientedImagesEnabled,
} = PSPCustomization;

const ViewerReqType = {
    PRJ_TYPE: 0,
    DST_TYPE: 1,
    OBJ_TYPE: 2,
    LWK_TYPE: 3,
    SNP_TYPE: 4,
    SHA_TYPE: 5,
};

interface InitTemplateOptions
    extends Pick<
        GViewInfo,
        | 'prj_id'
        | 'prj_name'
        | 'prj_token'
        | 'prj_storg_name'
        | 'prj_path'
        | 'dataset_id'
        | 'snapshot_id'
        | 'user_id'
        | 'permission'
        | 'org_id'
        | 'team_id'
        | 'org_style'
        | 'org_name'
        | 'org_website'
        | 'watermark'
        | 'watermark_text'
        | 'enable_orthofile'
        | 'enable_panorama'
    > {
    copyright_date: string;
    share_id?: string;
    share_token?: string;
    is_embed?: boolean;
    is_clean?: boolean;
}

interface InitPotreeTemplateResult {
    permissions: Record<any, any>;
}

export const DEFAULT_POINT_CLOUD_PARAMETERS = {
    POINT_SIZE_TYPE: 0, // src/defines.js line 36
    POINT_SIZE: 2,
    POINT_COLOR: 'rgba',
    BACKGROUND: 'rgb(30,30,30)',
    USE_EDL: true,
    USE_HQ: true,
};

const OSM_BACKGROUND = 'null';

export function InitPotreeTemplate(args: InitTemplateOptions): InitPotreeTemplateResult {
    setIdItemFromUrl();
    // SetCookieDontShowHelpDialog();

    const shSelectedDataset = sessionStorage.getItem('SH_SELECTED_DATASET');
    if (shSelectedDataset == null || shSelectedDataset == undefined || shSelectedDataset == '') {
        setDatasetId(args.dataset_id);
    } else {
        setDatasetId(shSelectedDataset);
    }

    setProjectId(args.prj_id);
    setProjectPath(args.prj_path);
    setProjectStorgName(args.prj_storg_name);
    setShareId(args.share_id);
    setUserId(args.user_id);
    setShareToken(args.share_token);
    setProjectToken(args.prj_token);
    setPermission(args.permission);
    setIsEmbed(args.is_embed);
    setIsClean(args.is_clean);
    setOrgId(args.org_id);
    setTeamId(args.team_id);
    setOrgName(args.org_name);
    setOrgWebsite(args.org_website);
    setCopyrightDate(args.copyright_date);
    setWatermark(args.watermark == 'true' ? true : false);
    setWatermarkText(args.watermark_text);
    setOrientedImagesEnabled(args.enable_orthofile || false);

    const userPermissionObj = JSON.parse(args.permission);

    //GET USERSTATE
    const urlParams = new URLSearchParams(window.location.search);
    const currentUserstate = urlParams.get('userstate');
    if (currentUserstate != null) {
        setSnapshotId(currentUserstate);
    }

    //SET DEFUALT VIEWER SETTING
    {
        const mainTitle = !args.org_id ? 'PointSharePlus' : args.org_name;
        // @ts-ignore
        document.maintitle = mainTitle;
        if (args.share_token != null && args.share_token != '') {
            document.title = mainTitle + ` Share Link [${args.prj_name}]`;
        } else {
            document.title = mainTitle + ` Viewer [${args.prj_name}]`;
        }

        viewer.setEDLEnabled(DEFAULT_POINT_CLOUD_PARAMETERS.USE_EDL);
        viewer.setFOV(60);
        viewer.setPointBudget(5 * 1000 * 1000);
        viewer.setMinNodeSize(0);
        if (PSPCustomization.getOsm()) {
            viewer.setBackground(OSM_BACKGROUND);
        } else {
            viewer.setBackground(DEFAULT_POINT_CLOUD_PARAMETERS.BACKGROUND);
        }
        // On mobile browsers, HQ renderer can prevent point clouds from showing.
        // Keep desktop behavior unchanged.
        viewer.useHQ = (PSPCustomization as any).isMobile?.() ? false : DEFAULT_POINT_CLOUD_PARAMETERS.USE_HQ;
        // Ensure the UI checkbox (if it already exists) matches the actual render setting.
        // Some restore paths rely on `resetUseHQ()` to sync the checkbox, but initial load can
        // happen without a snapshot restore.
        try {
            const hqCheckbox = document.getElementById('quality_options_hq') as HTMLInputElement | null;
            if (hqCheckbox) {
                hqCheckbox.checked = viewer.useHQ;
                hqCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
            }
        } catch (e) {
            // no-op (UI may not be mounted yet)
        }

        viewer.setDescription(`${args.prj_name}`);
        // viewer.setOrgName(`${args.org_name}`);
        viewer.loadSettingsFromURL();

        setCopyright();

        // viewer.loadGUI(() => {
        //     viewer.setLanguage('en');
        // });

        var $ProjectToken = PSPCustomization.getProjectToken();

        Potree.XHRFactory.config = {
            customHeaders: [
                { header: 'Authorization', value: `Bearer ${$ProjectToken}` },
                { header: 'XStorageName', value: args.prj_storg_name },
            ],
        };
    }

    function preAuthFetch(url: string, options: RequestInit) {
        return fetch(url, {
            headers: {
                Authorization: `Bearer ${$ProjectToken}`,
                XStorageName: args.prj_storg_name,
            },
            ...options,
        });
    }

    window.authFetch = preAuthFetch.bind(window);

    //DETEC AUTHORIZATION TOKEN
    {
        const userToken = localStorage.getItem('userToken');
        const shareToken = getShareToken();
        detectAuthToken(userToken, shareToken);
    }

    defViewerReqType();

    //FETCH AND LOAD LIST DATASETS
    {
        const projectId = PSPCustomization.getProjectId();
        actionFetchAndLoadListDatasetsByProjectId({
            projectId,
            token: getAuthToken() as string,
            onError: handleFetchListDatasetFail,
            onSuccess: handleFetchListDatasetSuccessful,
        });
    }

    return { permissions: userPermissionObj };
}

function setIdItemFromUrl() {
    const currUrl = window.location.pathname;
    const pathItems = currUrl.split('/');

    if (pathItems && pathItems.length >= 4) {
        const idItem = pathItems[4];
        if (idItem.indexOf('obj') >= 0) {
            const objId = idItem.replace('obj', '');
            setOBJId(objId);
        } else if (idItem.indexOf('lwk') >= 0) {
            const lwkId = idItem.replace('lwk', '');
            setLWKId(lwkId);
        }
    }
}

// function SetCookieDontShowHelpDialog() {
//     const defCokNoShowHelp = true;
//     PSPCustomization.pspUtils.SetCookie('DontShowHelpDialog', defCokNoShowHelp);
// }

function setCopyright() {
    const orgId = getOrgId();
    const copyrightDate = getCopyrightDate();

    const copyrightText = document.querySelector('#copyright_text') as HTMLElement;
    const copyrightLink = document.querySelector('#copyright_link') as HTMLElement;
    if (copyrightText) copyrightText.innerHTML = orgId ? 'Powered by' : `Copyright &copy ${copyrightDate}`;
    if (orgId && copyrightText) copyrightText.style.color = '#ffffff24';
    if (orgId && copyrightLink) copyrightLink.style.color = '#ffffff24';
    if (orgId) {
        if (copyrightText) copyrightText.style.display = 'none';
        if (copyrightLink) copyrightLink.style.display = 'none';
    }
}

function detectAuthToken(userToken?: string | null, shareToken?: string) {
    if (shareToken != null && shareToken != '') {
        setAuthToken(shareToken);
        setUserAuthType('SHARE');
    } else {
        setAuthToken(userToken);
        setUserAuthType('USER');
    }
}

function defViewerReqType() {
    const currUrl = window.location.pathname;
    const pathItems = currUrl.split('/');
    let typeRequest = null;
    const snapshotId = getSnapshotId();
    const sharId = getShareId();
    if (sharId != null) {
        typeRequest = ViewerReqType.SHA_TYPE;
    } else if (pathItems && pathItems.length >= 3) {
        const idItem = pathItems[4];
        if (snapshotId != null) {
            typeRequest = ViewerReqType.SNP_TYPE;
        } else if (idItem.indexOf('prj') >= 0) {
            typeRequest = ViewerReqType.PRJ_TYPE;
        } else if (idItem.indexOf('dst') >= 0) {
            typeRequest = ViewerReqType.DST_TYPE;
        } else if (idItem.indexOf('obj') >= 0) {
            typeRequest = ViewerReqType.OBJ_TYPE;
        } else if (idItem.indexOf('lwk') >= 0) {
            typeRequest = ViewerReqType.LWK_TYPE;
        }
    }

    setViewerReqType(typeRequest);
}

function handleFetchListDatasetSuccessful(data: ProjectDatasetResponse) {
    normalizeDatasets(data.response).then((listDatasets) => {
        loadDatasets(listDatasets);
    });
}

function handleFetchListDatasetFail() {
    viewer.postError('Fetch Datasets Fail, please contact your system administrator. ');
}

async function normalizeDatasets(datasets: ProjectDatasetResponse['response']) {
    const $ProjectId = PSPCustomization.getProjectId();
    const projectToken = PSPCustomization.getProjectToken();
    const $ProjectStorgName = PSPCustomization.getProjectStorgName();

    const listDatasets: NormalizedDataset[] = [];
    await Promise.all(
        datasets.map(async (datasetItem, index) => {
            try {
                const dsPath = API_PATHS.PSP.DL.DATASET_CHANK($ProjectId, datasetItem.id);
                const res = await checkFilename({
                    itemPathUrl: dsPath,
                    storageName: $ProjectStorgName,
                    projectToken: projectToken,
                    onError: (error) => {
                        console.log('[Error] checkExtation:', error);
                        if (error?.response?.status == 401) {
                            PSPCustomization.pspUtils.RedirectToLoginAddress();
                        }
                        PSPCustomization.EndWarningMesage();
                    },
                });
                const filenamedst = res?.filepath;
                let itmDataset: NormalizedDataset | undefined = undefined;

                if (filenamedst != null) {
                    const datasetPath = API_PATHS.PSP.DL.DATASET_DATA($ProjectId, datasetItem.id, filenamedst);

                    itmDataset = {
                        id: datasetItem.id,
                        index: index,
                        name: datasetItem.name,
                        path: datasetPath,
                        description: datasetItem.description,
                        number_of_points: datasetItem.number_of_points,
                        // set default point parameters
                        pointSize: DEFAULT_POINT_CLOUD_PARAMETERS.POINT_SIZE,
                        pointSizeType: DEFAULT_POINT_CLOUD_PARAMETERS.POINT_SIZE_TYPE,
                        activeAttributeName: DEFAULT_POINT_CLOUD_PARAMETERS.POINT_COLOR,
                        pointShape: Potree.PointShape.CIRCLE,
                        visible: true,
                        create_date: datasetItem.create_date,
                    } as NormalizedDataset;

                    listDatasets.push(itmDataset);
                }
                // and response need to be added into final response array
            } catch (error) {
                console.log('error' + error);
            }
        })
    );
    //console.log('complete all')
    return listDatasets;
}

async function setPoincloudInfo(pointcloud: any, index: number) {
    const $DatasetId = PSPCustomization.getDatasetId();
    const loadInfo = await Potree.loadPointCloud(`${apiHost}${pointcloud.path}`, pointcloud.name);

    // Set pointloud meta info
    const loadedPointCloud = loadInfo?.pointcloud;
    loadedPointCloud.name = pointcloud.name;
    loadedPointCloud.datasetId = pointcloud.id;
    loadedPointCloud.active = pointcloud.active;
    loadedPointCloud.visible = pointcloud.visible;

    loadedPointCloud.material.size = pointcloud.pointSize;
    loadedPointCloud.material.pointSizeType = pointcloud.pointSizeType;
    loadedPointCloud.material.activeAttributeName = pointcloud.activeAttributeName;
    loadedPointCloud.material.shape = pointcloud.pointShape;

    if (pointcloud.number_of_points) {
        loadedPointCloud.numberOfPoints = pointcloud.number_of_points;
    }
    //

    //SET DATASET ID WITH FIRTS DATASET
    // debugger;
    if ($DatasetId == null || $DatasetId == undefined || $DatasetId == '') {
        if (index == 0) {
            const firstDatasetId = loadedPointCloud.datasetId;
            PSPCustomization.setDatasetId(firstDatasetId);
            loadedPointCloud.active = true;
        } else {
            loadedPointCloud.active = false;
        }
    } else {
        const viewerReqType = PSPCustomization.getViewerReqType();
        let defVisible = false;
        switch (viewerReqType) {
            case ViewerReqType.PRJ_TYPE:
                defVisible = true;
                break;
            case ViewerReqType.DST_TYPE:
            case ViewerReqType.SNP_TYPE:
            case ViewerReqType.SHA_TYPE:
                defVisible = loadedPointCloud.datasetId === $DatasetId ? true : false;
                break;
        }

        loadedPointCloud.active = loadedPointCloud.datasetId == $DatasetId ? true : false;
        loadedPointCloud.visible = defVisible;
    }

    return loadedPointCloud;
}

function addPointCloudOnScene(pointcloud: any) {
    if (!pointcloud.projection) {
        pointcloud.projection = '+proj=longlat +datum=WGS84 +no_defs +type=crs';
    }

    viewer.scene.addPointCloud(pointcloud);
}

function addPointCloudToLayersTree(pointcloud: any) {
    viewer.scene.addHiddenPointCloud(pointcloud);
}

async function loadDatasets(pointclouds: any[]) {
    const $DatasetId = PSPCustomization.getDatasetId();
    const $ProjectId = PSPCustomization.getProjectId();

    //LOAD DATASETS USER BANDWIDTH LIMIT CHECK FOR EEROR SHOWING
    sessionStorage.removeItem('USER_BANDWIDTH_LIMIT');

    // LOAD DATASETS AS POINTCLOUD IN VIEWER
    if (pointclouds.length == 0) {
        /** INIT MAIN MENU AND SETTINGS **/
        PSPCustomization.initCustomMenu();
    } else {
        const notVisibleDatasets = [] as any[];
        const sortedPointClouds = pointclouds.filter((pointcloud) => {
            const isPointcloudVisible = isPointCloudVisible(pointcloud);

            if (!isPointcloudVisible) {
                notVisibleDatasets.push(pointcloud);
            }

            return isPointcloudVisible;
        }); // Load only visible datasets
        sortedPointClouds.sort((a: any, b: any) => {
            const timeA = new Date(a.create_date).getTime();
            const timeB = new Date(b.create_date).getTime();

            return timeB - timeA;
        });

        const loadedDatasets = await Promise.all(
            sortedPointClouds.map((pointcloud: any, index: number) => setPoincloudInfo(pointcloud, index))
        );

        if (PSPCustomization.getOsm()) {
            if (loadedDatasets[0]?.projection) {
                const pointcloudProjection = loadedDatasets[0]?.projection;
                const mapProjection = proj4.defs(OPEN_STREET_MAP_PROJECTION); // set coordinate system

                window.toMap = proj4(pointcloudProjection, mapProjection);
                window.toScene = proj4(mapProjection, pointcloudProjection);
            } else {
                if (!PSPCustomization.getOsmProjection()) {
                    viewer.postError("Project doesn't have a projection");
                }
            }
        }

        notVisibleDatasets.forEach(addPointCloudToLayersTree);
        loadedDatasets?.forEach(addPointCloudOnScene);

        loadedDatasets?.forEach((pointcloud) => {
            const $ProjectId = PSPCustomization.getProjectId();
            actionFetchAndLoadPanoramasByDatasetId({
                projectId: $ProjectId,
                datasetId: pointcloud.datasetId,
                token: getAuthToken() as string,
                onSuccess: (data) => {
                    pointcloud.panoramas = data;
                },
                onError: (err) => {
                    console.error('Failed to load panoramas: ', err);
                },
            });
        });

        const currentDataset = loadedDatasets.find((el) => el.datasetId === $DatasetId) || loadedDatasets[0];
        window.PPAppServices.currentDatasetService.setCurrentDataset(currentDataset);

        PSPCustomization.restoreDefaultSnapshot(false).then(() => {
            // fetch all snapshots & states
            PSPCustomization.initCustomMenu(); // init UI

            setTimeout(() => {
                PSPCustomization.setNeedSave(false);
            }, 1000); // set confirmation modal show to false after all objects update
        });

        viewer.fitToScreen();
    }
}

function isPointCloudVisible(pointcloud: any) {
    const $DatasetId = PSPCustomization.getDatasetId();
    const viewerReqType = PSPCustomization.getViewerReqType();
    let defVisible = false;

    switch (viewerReqType) {
        case ViewerReqType.PRJ_TYPE:
            defVisible = true;
            break;
        case ViewerReqType.SNP_TYPE:
        case ViewerReqType.SHA_TYPE:
            defVisible = pointcloud.visible;
            break;
        case ViewerReqType.DST_TYPE:
            defVisible = pointcloud.id === $DatasetId ? true : false;
            break;
    }

    return defVisible;
}
